import { Component, EventEmitter, Output, TemplateRef, ViewChild } from "@angular/core";
import { NgbModal, NgbModalOptions, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { ScheduleTimeSelectionType } from "src/app/core/constants/constants";
import { Helper } from "src/app/core/helpers/helper";

@Component({
  selector: "app-date-time-picker-modal",
  templateUrl: "./date-time-picker-modal.component.html",
  styleUrl: "./date-time-picker-modal.component.scss",
  standalone: false
})
export class DateTimePickerModalComponent {
  modalRef!: NgbModalRef;
  config: NgbModalOptions = { size: "md", centered: true, backdrop: "static", keyboard: false };

  defaultDate!: Date;
  minDate!: Date;
  maxDate!: Date;
  defaultDateString!: string;
  minDateString!: string;
  selectedDate!: any;
  selectionType!: ScheduleTimeSelectionType;
  selectedSlot!: string;
  selectedSlotRange!: string;
  ScheduleTimeSelectionType = ScheduleTimeSelectionType;
  bufferTime!: number;
  slotIntervalInMinutes!: number;

  @ViewChild("template", { static: true }) template!: TemplateRef<any>;
  @Output() dateSelected = new EventEmitter();
  timeSlots: string[] = [];
  slotRanges: string[] = [];
  isDateSelected!: boolean;

  constructor(private modalService: NgbModal, public helper: Helper) {}

  /**
   * Opens the date-time picker modal with specified buffer time and maximum booking days.
   * Initializes default, minimum, and maximum dates for the date picker.
   *
   * @param bufferTime The buffer time in minutes to add to the current date for the default date.
   * @param maxBookingDays The maximum number of days allowed for booking.
   */
  openModal({
    bufferTime = 30,
    maxBookingDays = 1,
    selectionType = ScheduleTimeSelectionType.TIME_PICKER,
    slotIntervalInMinutes = 10
  }: {
    bufferTime?: number;
    maxBookingDays: number;
    selectionType?: ScheduleTimeSelectionType;
    slotIntervalInMinutes?: number;
  }): void {
    const currentDate = new Date();

    const maxBookingDaysMinutes = maxBookingDays * 24 * 60 * 60000;

    this.defaultDate = new Date(currentDate.getTime() + bufferTime * 60000);
    this.minDate = new Date(currentDate.getTime() + bufferTime * 60000);
    this.maxDate = new Date(currentDate.getTime() + maxBookingDaysMinutes + bufferTime * 60000);
    this.maxDate.setHours(23, 59, 59, 999);
    this.defaultDateString = this.formatDate(this.defaultDate);
    this.minDateString = this.formatDate(this.minDate);
    this.selectedDate = this.defaultDate;
    this.selectionType = selectionType;
    this.slotIntervalInMinutes = slotIntervalInMinutes;
    this.bufferTime = bufferTime;
    this.isDateSelected = false;
    this.selectedSlotRange = "";
    this.selectedSlot = "";

    this.modalRef = this.modalService.open(this.template, this.config);
    this.modalRef.result.then(
      () => this.closeModal(),
      () => this.closeModal()
    );
  }

  /**
   * Emits the selected date and closes the modal.
   */
  selectDate(): void {
    let result: any;

    switch (this.selectionType) {
      case ScheduleTimeSelectionType.TIME_PICKER:
        result = this.selectedDate;
        break;

      case ScheduleTimeSelectionType.SLOT:
        if (!this.selectedSlot || !this.selectedDate) return;
        result = this.helper.combineDateAndTime(this.selectedDate, this.selectedSlot);
        break;

      case ScheduleTimeSelectionType.SLOT_RANGE: {
        if (!this.selectedSlotRange || !this.selectedDate) return;
        const startTime = this.selectedSlotRange.split(" - ")[0];
        result = this.helper.combineDateAndTime(this.selectedDate, startTime);
        break;
      }

      default:
        console.error("Invalid selection type", this.selectionType);
        return;
    }

    if (result) {
      this.dateSelected.emit(result);
      this.closeModal();
    }
  }

  /**
   * Formats a Date object into a string with the format 'Y-m-d H:i'.
   *
   * @param date The date object to format.
   * @returns The formatted date string.
   */
  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  /**
   * Event handler for when the flatpickr instance is ready.
   * Sets the minimum and default dates for the date picker.
   *
   * @param event The event object from the flatpickr instance.
   */
  onFlatpickrReady(event: any): void {
    const flatpickrInstance = event.instance;
    flatpickrInstance.set("minDate", this.minDateString);
    flatpickrInstance.set("defaultDate", this.defaultDateString);
  }

  /**
   * Event handler for when the date is changed in the date picker.
   * Updates the selected date.
   *
   * @param event The event object from the date picker.
   */
  onDateChange(event: any): void {
    this.selectedDate = event.target.value ? new Date(event.target.value) : this.defaultDate;
    this.isDateSelected = true;
    const isToday = this.helper.isSameDay(new Date(), this.selectedDate);
    const bufferTimeToUse = isToday ? this.bufferTime : 0;

    if (this.selectionType === ScheduleTimeSelectionType.SLOT) {
      this.timeSlots = this.generateTimeSlots(this.slotIntervalInMinutes, bufferTimeToUse, this.selectedDate);
    } else if (this.selectionType === ScheduleTimeSelectionType.SLOT_RANGE) {
      this.slotRanges = this.generateSlotRanges(this.slotIntervalInMinutes, bufferTimeToUse, this.selectedDate);
    }
  }

  /**
   * Generates an array of string time slots based on the provided slot interval,
   * buffer time, and selected date.
   *
   * If the selected date is today, the start time is calculated by adding the
   * specified buffer time to the current time and aligning it to the next slot
   * interval. Otherwise, the start time is set to midnight.
   *
   * Each slot is formatted in 12-hour time with an "AM/PM" designation.
   *
   * @param slotIntervalInMinutes The interval in minutes for each slot.
   * @param bufferTime The buffer time in minutes to add to the current time for
   *   today's date.
   * @param selectedDate The date for which to generate the time slots.
   * @returns An array of formatted time slots as strings.
   */
  generateTimeSlots(slotIntervalInMinutes: number, bufferTime: number, selectedDate: Date): string[] {
    const startTime = new Date(selectedDate);
    const now = new Date();

    if (this.helper.isSameDay(now, selectedDate)) {
      const buffered = new Date(now.getTime() + bufferTime * 60000);

      // Get total minutes since midnight
      const totalMinutes = buffered.getHours() * 60 + buffered.getMinutes();

      // Round up to next slot interval using your formula
      const roundedMinutes = Math.floor((totalMinutes + slotIntervalInMinutes - 1) / slotIntervalInMinutes) * slotIntervalInMinutes;

      // Set hours and minutes back into startTime
      const hours = Math.floor(roundedMinutes / 60);
      const minutes = roundedMinutes % 60;

      startTime.setHours(hours, minutes, 0, 0);
    } else {
      startTime.setHours(0, 0, 0, 0);
    }

    const endTime = new Date(selectedDate);
    endTime.setHours(23, 59, 59, 999);

    const slots: string[] = [];
    const currentSlot = new Date(startTime);

    while (currentSlot <= endTime) {
      const hours = currentSlot.getHours().toString().padStart(2, "0");
      const minutes = currentSlot.getMinutes().toString().padStart(2, "0");
      const formatted = this.formatTo12HourTime(hours, minutes);
      slots.push(formatted);
      currentSlot.setMinutes(currentSlot.getMinutes() + slotIntervalInMinutes);
    }

    return slots;
  }

  /**
   * Sets the selected slot to the specified value.
   *
   * @param slot The time slot to be selected.
   */

  selectSlot(slot: string): void {
    this.selectedSlot = slot;
  }

  selectSlotRange(slotRange: string): void {
    this.selectedSlotRange = slotRange;
  }

  /**
   * Formats a given time in 24-hour format to 12-hour format with AM/PM.
   *
   * @param hours The hour of the day in 24-hour format (0-23).
   * @param minutes The minutes of the hour (0-59).
   * @returns The formatted time string in "HH:MM AM/PM" format.
   */
  formatTo12HourTime(hours: string | number, minutes: string | number): string {
    const h = typeof hours === "string" ? parseInt(hours, 10) : hours;
    const m = typeof minutes === "string" ? parseInt(minutes, 10) : minutes;
    const period = h >= 12 ? "PM" : "AM";
    const formattedHour = (((h + 11) % 12) + 1).toString().padStart(2, "0");
    const formattedMinute = m.toString().padStart(2, "0");
    return `${formattedHour}:${formattedMinute} ${period}`;
  }

  /**
   * Generates an array of string representations of time slot ranges, rounded up to the next multiple of the specified slot interval.
   * If the selected date is today, the start time is rounded up to the next multiple of the slot interval, starting from the current time.
   * If the selected date is not today, the start time is set to midnight.
   *
   * @param slotIntervalInMinutes The time interval in minutes between each slot.
   * @param bufferTime The buffer time in minutes to add to the current time for the default date.
   * @param selectedDate The selected date.
   * @returns An array of string representations of time slot ranges in "HH:MM AM/PM - HH:MM AM/PM" format.
   */
  generateSlotRanges(slotIntervalInMinutes: number, bufferTime: number, selectedDate: Date): string[] {
    const now = new Date();
    const startTime = new Date(selectedDate);
    const isToday = this.helper.isSameDay(now, selectedDate);

    if (isToday) {
      const buffered = new Date(now.getTime() + bufferTime * 60000);

      // Total minutes since midnight
      const totalMinutes = buffered.getHours() * 60 + buffered.getMinutes();

      // Round up to next multiple of slotIntervalInMinutes
      const roundedMinutes = Math.floor((totalMinutes + slotIntervalInMinutes - 1) / slotIntervalInMinutes) * slotIntervalInMinutes;

      // Set hours and minutes to rounded time
      const roundedHours = Math.floor(roundedMinutes / 60);
      const roundedMins = roundedMinutes % 60;

      startTime.setHours(roundedHours, roundedMins, 0, 0);
    } else {
      startTime.setHours(0, 0, 0, 0); // start at midnight
    }

    const endTime = new Date(selectedDate);
    endTime.setHours(23, 59, 59, 999);

    const ranges: string[] = [];
    const slotStart = new Date(startTime);

    while (slotStart.getTime() + slotIntervalInMinutes * 60000 <= endTime.getTime()) {
      const slotEnd = new Date(slotStart.getTime() + slotIntervalInMinutes * 60000);

      const formattedRange = `${this.formatTo12HourTime(slotStart.getHours(), slotStart.getMinutes())} - ${this.formatTo12HourTime(slotEnd.getHours(), slotEnd.getMinutes())}`;

      ranges.push(formattedRange);

      slotStart.setMinutes(slotStart.getMinutes() + slotIntervalInMinutes);
    }

    return ranges;
  }

  /**
   * Closes the date-time picker modal.
   */
  closeModal(): void {
    this.modalRef.close();
  }
}
